<template>
  <div class="animated fadeIn page-container">
    <h1>Congestion Summary</h1>

    <!-- filters panel -->
    <filter-panel
      class="filters-panel"
      :filter-parameters="computedFilterParameters"
      @update="applyFilter"
      :notification-text="null"
      :api-request-data="null"/>

    <!-- summary table -->
    <b-card no-body class="w-100">
      <b-card-body class="card-content">
        <c-table
          :table-data="tableData.rows"
          :fields="columnsFiltered"
          :cell-formatter="tableCellFormatter"
          :cell-class-handler="tableCellClassHandler"
          :striped="true"
          :bordered="true"
          :small="false"
          :perPage=30
          :hover="true"
          caption="">
        </c-table>
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import isEmpty from 'lodash-es/isEmpty';
import Numeral from 'numeral';
import { mapActions, mapGetters } from 'vuex';
import CTable from '../components/Table';
import FilterPanel from '../components/widgets/FilterPanel';

import {
  getAPIFilterValues,
  applyFilterValues,
  getFormattedFilters
} from '../components/widgets/FilterPanel';
import { formatVolume } from '@/shared/number-utils';
import getNested from 'lodash-es/get';

export default {
  name: 'congestion-summary',
  components: {
    CTable,
    FilterPanel,
  },
  data() {
    const segmentChoices = [
      {
        'text': 'Capesize',
        'value': 'capesize'
      },
      {
        'text': 'Handysize',
        'value': 'handysize'
      },
      {
        'text': 'Panamax',
        'value': 'panamax'
      },
      {
        'text': 'Supramax',
        'value': 'supramax'
      },
    ];

    const defaultChoices = {
      locationType: {text: '-- Location type --', value: null},
      metric: {text: '-- Metric --', value: null},
      segment: segmentChoices,
    };

    return {
      filterSchema: {
        locationType: {
          name: 'locationType',
          apiName: 'location_type',
          type: 'select-buttons',
          label: 'Location type:',

          value: 'region',  // override default choice
          choices: [
            // defaultChoices.locationType,
            {text: 'Region', value: 'region'},
            {text: 'Country', value: 'country'},
            {text: 'Port', value: 'port'},
          ],
          // defaultChoice: defaultChoices.locationType.value,
        },

        metric: {
          name: 'metric',
          apiName: 'metric',
          type: 'select-buttons',
          label: 'Metric:',

          value: 'vessel_count',  // override default choice
          choices: [
            // defaultChoices.metric,
            {text: 'Vessel count', value: 'vessel_count'},
            {text: 'Vessel DWT', value: 'vessel_dwt'},
          ],
          // defaultChoice: defaultChoices.metric.value,
        },

        segment: {
          name: 'segment',
          apiName: 'segment',
          type: 'multiselect',
          placeholder: 'Segment',

          value: segmentChoices,  // override default choice
          choices: segmentChoices,
          defaultChoice: defaultChoices.segment,
        },
      },

      tableData: {
        // country, country_code
        // port_id, port_name
        columns: [
          {
            key: 'countryCode',
            label: 'Country Code',
            sortable: true
          },
          {
            key: 'country',
            label: 'Country',
            sortable: true
          },
          {
            key: 'portId',
            label: 'Port ID',
            sortable: true
          },
          {
            key: 'portName',
            label: 'Port Name',
            sortable: true
          },
          {
            key: 'regionId',
            label: 'Region ID',
            sortable: true
          },
          {
            key: 'regionName',
            label: 'Region Name',
            sortable: true
          },
          {
            key: 'today',
            label: 'Today',
            sortable: true,
            class: 'text-right',
          },
          {
            key: 'tMinus7',
            label: '7 Days Ago',
            sortable: true,
            class: 'text-right',
          },
          {
            key: 'tMinus7Change',
            label: 'Change',
            sortable: true,
            class: 'text-right',
          },
          {
            key: 'tMinus30',
            label: '30 Days Ago',
            sortable: true,
            class: 'text-right',
          },
          {
            key: 'tMinus30Change',
            label: 'Change',
            sortable: true,
            class: 'text-right',
          },
          {
            key: 'tMinus365',
            label: '365 Days Ago',
            sortable: true,
            class: 'text-right',
          },
          {
            key: 'tMinus365Change',
            label: 'Change',
            sortable: true,
            class: 'text-right',
          },
        ],
        rows: [],
      },
    };
  },

  // async mounted() {
  //   if(this.getActivePreset) {
  //     applyFilterValues(this.filterSchema, this.getActivePreset.params);
  //   }
  // },

  methods: {
    ...mapActions('api/congestion', [
      'getCongestionSummary',
    ]),

    async applyFilter(filterValues) {
      if(!this.isFilterValid(filterValues)) {
        this.tableData.rows = [];
        return;
      }

      const apiFilterValues = getAPIFilterValues(this.filterSchema, filterValues);
      let response = await this.getCongestionSummary(apiFilterValues);

      if(response && response.data.records) {
        this.tableData.rows = this.addGlobalRow(response.data.records);
        this.tableData.rows = this.formatRecords(this.tableData.rows);
      } else {
        this.tableData.rows = [];
      }

      applyFilterValues(this.filterSchema, filterValues);
    },

    isFilterValid(filterValues) {
      return !(
        isEmpty(filterValues.locationType) ||
        isEmpty(filterValues.metric) ||
        isEmpty(filterValues.segment)
      );
    },

    formatRecords(records) {
      return records.map(item => {
        item.t_minus_7_change = item.today - item.t_minus_7;
        item.t_minus_30_change = item.today - item.t_minus_30;
        item.t_minus_365_change = item.today - item.t_minus_365;

        if(item.country !== undefined) {
          item.country = item.country ? item.country : '--';
        }

        return item;
      });
    },

    addGlobalRow(records) {
      let globalRecord = {
        countryCode: 'NA',
        country: 'Total Global Congestion',
        portId: 'NA',
        portName: 'Total Global Congestion',
        regionId: 'NA',
        regionName: 'Total Global Congestion',
        _rowVariant: 'global-row',
      };
      const excludedFieldNames = Object.keys(globalRecord);

      records.forEach((record) => {
        Object.keys(record).forEach((fieldName) => {
          if(excludedFieldNames.includes(fieldName)) {
            // some field names could be numeric, like port_id
            return;
          }

          if(typeof record[fieldName] === 'number') {
            if(globalRecord[fieldName] === undefined) {
              globalRecord[fieldName] = 0;
            }
            globalRecord[fieldName] += record[fieldName];
          }
        });
      });

      return [...records, globalRecord];
    },

    tableCellFormatter(cell) {
      if(typeof cell.value === 'number') {
        const isDecimal = cell.value < Numeral('100m').value();
        return formatVolume(cell.value, '100k', isDecimal)
      } else {
        return cell.value;
      }
    },

    tableCellClassHandler(cell) {
      const fieldNameLower = cell.field.key.toLowerCase();
      const fieldValue = cell.value;

      if(fieldNameLower.endsWith('_change')) {

        if(fieldValue > 0) {
          return 'text-success';
        } else if(fieldValue < 0) {
          return 'text-danger';
        }
      }
    },
  },

  computed: {
    ...mapGetters('api/presets', ['getActivePreset']),

    computedFilterParameters() {
      return getFormattedFilters(this.filterSchema);
    },

    columnsFiltered() {
      if(this.tableData.rows.length) {
        const seenColumns = Object.keys(this.tableData.rows[0]);
        return this.tableData.columns.filter(column => {
          return seenColumns.includes(column.key);
        });
      } else {
        return this.tableData.columns;
      }
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        // if(!this.filtersReady) {
        //   await this.loadFilters();
        //   if(!preset) {
        //     this.setDefaultValues();
        //   }
        // }

        if(preset) {
          const filterValues = getNested(preset, 'params.filterPanel', {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      },
    },
  },
};
</script>

<style scoped>
.page-container >>> .card-content {
  min-height: 460px;
}

.card-content >>> .bg-global-row {
  font-weight: bold;
}

.filters-panel >>> .multiselect__tags {
  min-width: 300px;
}
</style>

<style scoped lang="scss">
</style>
